/**
 * Branding store — palette, company name, tagline, logo URL.
 *
 * Palettes are RGB-channel values (no # prefix, no rgb()) so they work
 * directly with Tailwind's opacity modifier:  bg-brand-600/20
 *
 * On first load  → apply CSS vars from localStorage (no flash).
 * In background  → fetch /branding from API and sync to store + localStorage.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Palette definitions
// ---------------------------------------------------------------------------

export interface Palette {
  key: string;
  name: string;
  swatch: string;            // hex for UI display only
  vars: Record<string, string>; // CSS var name → "R G B"
}

export const PALETTES: Palette[] = [
  {
    key: 'forest-green', name: 'Forest Green', swatch: '#1E8A5B',
    vars: { '--brand-400': '52 175 110', '--brand-500': '30 138 91', '--brand-600': '22 105 68', '--brand-700': '16 76 50' },
  },
  {
    key: 'crimson', name: 'Crimson', swatch: '#dc2626',
    vars: { '--brand-400': '248 113 113', '--brand-500': '239 68 68', '--brand-600': '220 38 38', '--brand-700': '185 28 28' },
  },
  {
    key: 'rose', name: 'Rose', swatch: '#e11d48',
    vars: { '--brand-400': '251 113 133', '--brand-500': '244 63 94', '--brand-600': '225 29 72', '--brand-700': '190 18 60' },
  },
  {
    key: 'orange', name: 'Orange', swatch: '#ea580c',
    vars: { '--brand-400': '251 146 60', '--brand-500': '249 115 22', '--brand-600': '234 88 12', '--brand-700': '194 65 12' },
  },
  {
    key: 'amber', name: 'Amber', swatch: '#d97706',
    vars: { '--brand-400': '251 191 36', '--brand-500': '245 158 11', '--brand-600': '217 119 6', '--brand-700': '180 83 9' },
  },
  {
    key: 'emerald', name: 'Emerald', swatch: '#059669',
    vars: { '--brand-400': '52 211 153', '--brand-500': '16 185 129', '--brand-600': '5 150 105', '--brand-700': '4 120 87' },
  },
  {
    key: 'teal', name: 'Teal', swatch: '#0d9488',
    vars: { '--brand-400': '45 212 191', '--brand-500': '20 184 166', '--brand-600': '13 148 136', '--brand-700': '15 118 110' },
  },
  {
    key: 'blue', name: 'Blue', swatch: '#2563eb',
    vars: { '--brand-400': '96 165 250', '--brand-500': '59 130 246', '--brand-600': '37 99 235', '--brand-700': '29 78 216' },
  },
  {
    key: 'violet', name: 'Violet', swatch: '#7c3aed',
    vars: { '--brand-400': '167 139 250', '--brand-500': '139 92 246', '--brand-600': '124 58 237', '--brand-700': '109 40 217' },
  },
  {
    key: 'indigo', name: 'Indigo', swatch: '#4f46e5',
    vars: { '--brand-400': '129 140 248', '--brand-500': '99 102 241', '--brand-600': '79 70 229', '--brand-700': '67 56 202' },
  },
  {
    key: 'pink', name: 'Pink', swatch: '#db2777',
    vars: { '--brand-400': '244 114 182', '--brand-500': '236 72 153', '--brand-600': '219 39 119', '--brand-700': '190 24 93' },
  },
];

export function getPalette(key: string): Palette {
  return PALETTES.find((p) => p.key === key) ?? PALETTES[0]!;
}

/** Apply a palette by setting the  data-palette  attribute on <html>.
 *  The CSS rules in styles.css use html[data-palette="*"] selectors with
 *  specificity (0,1,1) which reliably override the :root defaults (0,1,0).
 *  This is the same pattern as dark mode (class on <html>) and ensures
 *  every element that uses a brand-* Tailwind class updates immediately. */
export function applyPalette(key: string): void {
  // Remove any legacy inline-style overrides left by older code
  for (const v of ['--brand-400', '--brand-500', '--brand-600', '--brand-700']) {
    document.documentElement.style.removeProperty(v);
  }
  document.documentElement.dataset.palette = key;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export interface BrandingState {
  paletteKey: string;
  companyName: string;
  tagline: string;
  logoUrl: string | null;
  loaded: boolean;

  setPalette: (key: string) => void;
  setBranding: (b: Partial<Pick<BrandingState, 'paletteKey' | 'companyName' | 'tagline' | 'logoUrl'>>) => void;
}

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set) => ({
      paletteKey: 'forest-green',
      companyName: 'Bodhini',
      tagline: '',
      logoUrl: null,
      loaded: false,

      setPalette: (key) => {
        applyPalette(key);
        set({ paletteKey: key });
      },

      setBranding: (b) => {
        set({ ...b, loaded: true });
      },
    }),
    {
      name: 'bodhini-branding',
      partialize: (s) => ({
        paletteKey: s.paletteKey,
        companyName: s.companyName,
        tagline: s.tagline,
        logoUrl: s.logoUrl,
      }),
    },
  ),
);

/**
 * Call once on app boot to clear any stale data-palette attribute left in
 * localStorage from older builds. The design-locked Warm Trail theme in
 * styles.css :root is the canonical source of truth — no runtime override.
 */
export function initBranding(): void {
  delete document.documentElement.dataset.palette;
}
